
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export interface Factory {
  id: string;
  name: string;
  factory_number?: string | null;
  location?: string | null;
  contact_person?: string | null;
  phone?: string | null;
  email?: string | null;
  active: boolean;
  capacity?: number | null;
  created_at?: string;
  updated_at?: string;
}

export const useFactories = () => {
  const [factories, setFactories] = useState<Factory[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const fetchFactories = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from("factories")
        .select("*")
        .order("name");

      if (error) throw error;
      setFactories(data || []);
    } catch (error: any) {
      toast({
        title: "Error fetching factories",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const checkFactoryNumberExists = async (factoryNumber: string, excludeId?: string) => {
    const query = supabase
      .from("factories")
      .select("id")
      .eq("factory_number", factoryNumber);
    
    if (excludeId) {
      query.neq("id", excludeId);
    }
    
    const { data, error } = await query;
    if (error) throw error;
    return data && data.length > 0;
  };

  const createFactory = async (factoryData: Partial<Factory>) => {
    try {
      // Check if factory_number is unique
      if (factoryData.factory_number) {
        const exists = await checkFactoryNumberExists(factoryData.factory_number);
        if (exists) {
          toast({
            title: "Error",
            description: "Factory number already exists. Please use a different number.",
            variant: "destructive",
          });
          return false;
        }
      }

      const { error } = await supabase
        .from("factories")
        .insert({
          name: factoryData.name,
          factory_number: factoryData.factory_number || null,
          location: factoryData.location || null,
          contact_person: factoryData.contact_person || null,
          phone: factoryData.phone || null,
          email: factoryData.email || null,
          active: factoryData.active,
          capacity: factoryData.capacity !== null ? Number(factoryData.capacity) : null
        });

      if (error) throw error;
      toast({
        title: "Factory Created",
        description: `${factoryData.name} has been created.`
      });
      return true;
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
      return false;
    }
  };

  const updateFactory = async (factoryId: string, factoryData: Partial<Factory>) => {
    try {
      // Check if factory_number is unique (excluding current factory)
      if (factoryData.factory_number) {
        const exists = await checkFactoryNumberExists(factoryData.factory_number, factoryId);
        if (exists) {
          toast({
            title: "Error",
            description: "Factory number already exists. Please use a different number.",
            variant: "destructive",
          });
          return false;
        }
      }

      const { error } = await supabase
        .from("factories")
        .update({
          name: factoryData.name,
          factory_number: factoryData.factory_number || null,
          location: factoryData.location || null,
          contact_person: factoryData.contact_person || null,
          phone: factoryData.phone || null,
          email: factoryData.email || null,
          active: factoryData.active,
          capacity: factoryData.capacity !== null ? Number(factoryData.capacity) : null,
          updated_at: new Date().toISOString()
        })
        .eq("id", factoryId);

      if (error) throw error;
      toast({
        title: "Factory Updated",
        description: `${factoryData.name} has been updated.`
      });
      return true;
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
      return false;
    }
  };

  const deleteFactory = async (factory: Factory) => {
    try {
      const { data: users, error: usersError } = await supabase
        .from("users")
        .select("id")
        .eq("factory_id", factory.id)
        .limit(1);

      if (usersError) throw usersError;
      
      if (users && users.length > 0) {
        toast({
          title: "Cannot Delete",
          description: "This factory has associated users and cannot be deleted.",
          variant: "destructive",
        });
        return false;
      }
      
      const { data: orders, error: ordersError } = await supabase
        .from("orders")
        .select("id")
        .eq("factory_id", factory.id)
        .limit(1);

      if (ordersError) throw ordersError;
      
      if (orders && orders.length > 0) {
        toast({
          title: "Cannot Delete",
          description: "This factory has associated orders and cannot be deleted.",
          variant: "destructive",
        });
        return false;
      }

      const { error } = await supabase
        .from("factories")
        .delete()
        .eq("id", factory.id);

      if (error) throw error;
      return true;
    } catch (error: any) {
      toast({
        title: "Error deleting factory",
        description: error.message,
        variant: "destructive",
      });
      return false;
    }
  };

  useEffect(() => {
    fetchFactories();
  }, []);

  return {
    factories,
    loading,
    fetchFactories,
    createFactory,
    updateFactory,
    deleteFactory
  };
};
